// S8 — go-live checklist i18n. Merged INTO the existing `onboarding.checklist`
// block in `onboarding.i18n.ts`, so every key here is reachable as
// `onboarding.checklist.*` in all four locales and gated by `i18n:parity`.
//
// Two key families are dictated by the API and must not be renamed:
//  - `items.<category>.<name>.title` — `checklistItemLabelKey(item_key)` builds
//    `onboarding.checklist.items.<item_key>.title`, and an item key contains a
//    dot, which i18next reads as nesting. Hence the two-level shape.
//  - `rule.<category>.<name>` — the evaluator ships `detail.rule_key =
//    onboarding.checklist.rule.<item_key>` (§3.6: "the API never ships
//    translated text as the only signal"), so these keys ARE the contract. Same
//    nesting rule applies.
// `module.<n>_<name>` mirrors `detail.blocked_on_module_key` with its `module.`
// prefix stripped (i18next would read the dot as nesting).
//
// The rule strings are the §3.6 item table said to a factory admin rather than
// to a reviewer: what the check wants, not how it is computed.

export const checklistScreenEn = {
  // ---- item titles (the closed 19-item catalog, §3.6) ----------------------
  items: {
    vertical: {
      pack_installed: { title: "Industry pack installed" },
    },
    quality: {
      checks_reviewed: { title: "Quality checks reviewed" }, // D-214
    },
    catalogue: {
      products_min: { title: "At least one product" },
      boms_cover_products: { title: "Bills of materials cover your products" },
      routings_cover_products: { title: "Routings cover your products" },
      routings_bound: { title: "Every routing step points at a real work center" },
    },
    plant: {
      facility_min: { title: "At least one site" },
      departments_min: { title: "Every site has a department" },
      work_centers_min: { title: "Every production department has a work center" },
      machines_calibration: { title: "Calibrated machines have a meter unit" },
    },
    people: {
      admin_per_facility: { title: "Every site has an admin" },
      floor_workers_min: { title: "Every site with work centers has a floor worker" },
      invites_ready: { title: "No invitation is stuck" },
    },
    opening: {
      stock_entered: { title: "Opening stock entered" },
      stock_valued: { title: "Opening stock is valued" },
      open_orders_entered: { title: "Open sales orders entered" },
      customers_entered: { title: "Customers entered" },
    },
    channels: {
      qr_kit_printed: { title: "QR label kit printed" },
      floor_pwa_tested: { title: "Floor app tested with a real scan" },
      notification_channel: { title: "Notification channel connected" },
    },
  },

  // ---- the rule each verdict applied (`detail.rule_key`) -------------------
  rule: {
    vertical: {
      pack_installed: "One industry pack must be installed.",
    },
    quality: {
      // D-214 — the decision id stays HERE, not in the tenant's sentence.
      checks_reviewed:
        "Step 5 must be confirmed — someone reviewed the installed quality checks and their bands.",
    },
    catalogue: {
      products_min: "You need at least one product that is not discontinued.",
      boms_cover_products:
        "At least 80% of the products you make need an active bill of materials.",
      routings_cover_products: "At least 80% of the products you make need an active routing.",
      routings_bound:
        "Every step of every active routing must point at a work center that still exists.",
    },
    plant: {
      facility_min: "You need at least one site.",
      departments_min: "Every site needs at least one department.",
      work_centers_min:
        "You need at least one work center, and every department of a production site needs one. Office and warehouse sites are not counted.",
      machines_calibration: "Every machine with calibration tracking needs a meter unit.",
    },
    people: {
      admin_per_facility: "Every site needs an admin — a member or a pending invitation.",
      floor_workers_min: "Every site that has work centers needs at least one floor worker.",
      invites_ready: "No invitation may be left expired or undeliverable.",
    },
    opening: {
      stock_entered: "Enter your opening stock, or waive this with a reason.",
      stock_valued: "Every opening stock row needs a value.",
      open_orders_entered: "Enter your open sales orders, or waive this with a reason.",
      customers_entered: "You need at least one customer.",
    },
    channels: {
      qr_kit_printed: "Print at least one QR label kit.",
      floor_pwa_tested:
        "Someone must scan a station QR code and finish one test step on the floor app.",
      notification_channel: "An admin confirms the messaging bot is connected.",
    },
  },

  // ---- owning modules (`detail.blocked_on_module_key`) ---------------------
  module: {
    "01_catalogue": "Catalogue",
    "02_qr_kits": "QR label kits",
    "04_production": "Production",
    "06_inventory": "Inventory",
    "08_maintenance": "Maintenance",
    "09_sales": "Sales & CRM",
    "10_procurement": "Procurement",
    "11_finance": "Finance",
    "12_notifications": "Notifications",
    "16_identity": "People & sites",
  },

  // ---- evidence field labels (`detail`, minus the plumbing) ---------------
  evidence: {
    count: "Found",
    required: "Required",
    active: "Active",
    discontinued: "Discontinued",
    total_rows: "Rows in total",
    installed_packs: "Installed packs",
    pack_key: "Pack",
    pack_version: "Pack version",
    manufactured_products: "Products you make",
    with_active_bom: "With an active bill of materials",
    with_active_routing: "With an active routing",
    coverage_pct: "Coverage %",
    required_pct: "Coverage needed %",
    active_routings: "Active routings",
    active_routing_steps: "Active routing steps",
    referenced_work_centers: "Work centers referenced",
    unbound_work_center_ids: "Missing work centers",
    facilities: "Sites",
    facilities_without_department: "Sites with no department",
    active_work_centers: "Active work centers",
    departments_without_work_center: "Departments with no work center",
    non_production_departments_skipped: "Departments not counted (not production)",
    calibration_tracked_machines: "Machines with calibration tracking",
    missing_meter_unit: "Missing a meter unit",
    group_scope_admin: "Group-wide admin",
    facilities_without_admin: "Sites with no admin",
    facilities_with_work_centers: "Sites with work centers",
    group_scope_floor_worker: "Group-wide floor worker",
    facilities_without_floor_worker: "Sites with no floor worker",
    stuck_invites: "Stuck invitations",
    invites_by_status: "Invitations by status",
    delivery_failure_tracking: "Delivery failures tracked",
    rows: "Rows",
    rows_with_value: "Rows with a value",
    ready_batches: "Label kits ready",
    test_operation_completed: "Test step completed",
    attestation: "Attestation",
  },

  // ---- row chrome ----------------------------------------------------------
  weight: "Weight {{weight}}",
  weightTotal: "Scored out of {{total}} weight across all checks.",
  sectionWeight: "{{earned}} of {{total}} weight met",
  sectionItems: "Items: {{n}}",
  manualItem: "Set by hand",
  showDetail: "Show detail",
  hideDetail: "Hide detail",
  evaluatedAt: "Checked {{time}}",
  neverEvaluatedItem: "Not checked yet.",
  noEvidence: "No detail recorded for this check yet.",
  vacuous: "Nothing to check here yet, so this passes.",
  blockedOnModule: "Waiting on {{module}} — nobody can complete this check yet.",
  waivedReason: "Waived: {{reason}}",

  // ---- empty state (never evaluated) --------------------------------------
  emptyTitle: "Nothing has been checked yet",
  emptyBody:
    "These are the checks that decide whether you can go live. Run the evaluation to fill them in.",
  emptyNoPermission: "Ask an admin to run the evaluation.",

  // ---- blocking summary ----------------------------------------------------
  blockingTitle: "Blocking checks outstanding",
  blockingBody: "You cannot go live until each of these passes or is waived by a group admin.",

  // ---- actions -------------------------------------------------------------
  actions: {
    evaluate: "Run evaluation",
    evaluating: "Checking…",
    waive: "Waive",
    waiving: "Waiving…",
    waiveConfirm: "Waive this check",
    unwaive: "Remove waiver",
    attestPassed: "Mark as done",
    attestFailed: "Mark as not done",
    testScanOk: "Test scan OK",
    cancel: "Cancel",
  },

  waiveTitle: "Waive this check",
  waiveBody: "Say why this gap is acceptable. The reason is recorded against your name.",
  waiveBlockingBody:
    "This is a blocking check, so only a group admin can waive it — and the reason is recorded against your name.",
  waiveReasonLabel: "Reason",
  waiveReasonHint: "At least {{min}} characters. You have written {{count}}.",

  attestUnavailable:
    "This can't be confirmed in this workspace. You can still mark it not done, or waive it.",

  // ---- go-live pre-flight --------------------------------------------------
  goLiveHeading: "Go live",
  preflight: {
    stepsDone: "All {{total}} setup steps are complete.",
    stepsPending: "These setup steps are not complete: {{steps}}.",
    blockingClear: "No blocking checks outstanding.",
    blockingUnmet: "Blocking checks outstanding: {{count}}.",
    scoreOk: "Completeness {{score}} of {{required}} needed.",
    scoreGap: "Completeness {{score}}; {{required}} is needed ({{gap}} to go).",
    serverEligible: "Every check says you are ready to go live.",
    serverNotEligible: "Not cleared to go live yet.",
  },

  // ---- live (frozen) state -------------------------------------------------
  liveHistory: "Your factory went live with a completeness score of {{score}}.",
  liveBy: "Taken live by {{user}}.",

  // ---- failures ------------------------------------------------------------
  errors: {
    loadTitle: "Could not load the checklist",
    loadDetail: "The go-live checks could not be read. Try again.",
    blockingWaiveForbidden: "Only a group admin can waive a blocking check.",
    itemNotManual:
      "This check is decided by the evaluation, not by hand. Fix what it asks for, or waive it with a reason.",
    checkNotAvailable: "This can't be confirmed in this workspace.",
    alreadyLive: "Your factory is already live. The checklist is kept as a record.",
    busy: "Another setup change is running. Try again in a moment.",
    reasonTooShort: "That was not accepted. Check the reason and try again.",
    generic: "That action did not go through. Try again.",
  },

  // Where to go and fix a failed item (§4 S8 "sample links").
  fixThis: "Fix this →",
  // The contract carries only the actor's uuid; the copy labels it as one
  // rather than presenting a machine id as a person's name.
  liveOn: "Your factory went live on {{date}} with a completeness score of {{score}}.",
} as const;
